import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReview } from '../../context/ReviewContext';
import { reviewUtils } from '../../data/reviewModel';

const ReviewCard = ({ review, showResponse = true, canRespond = false }) => {
  const { t } = useTranslation();
  const { respondToReview } = useReview();
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [isResponding, setIsResponding] = useState(false);

  const handleRespond = async (e) => {
    e.preventDefault();
    
    if (!responseText.trim()) return;

    console.log('ReviewCard: Review object:', review);
    console.log('ReviewCard: Review ID:', review.id);
    console.log('ReviewCard: Review ID type:', typeof review.id);
    
    if (!review.id || review.id.trim() === '') {
      console.error('ReviewCard: Review ID is undefined, null, or empty string');
      console.error('ReviewCard: Review ID value:', `"${review.id}"`);
      alert('เกิดข้อผิดพลาด: ไม่พบ ID ของรีวิว');
      return;
    }

    console.log('ReviewCard: Attempting to respond to review:', review.id, 'with text:', responseText);
    setIsResponding(true);
    try {
      await respondToReview(review.id, responseText);
      setShowResponseForm(false);
      setResponseText('');
    } catch (error) {
      console.error('Error responding to review:', error);
    } finally {
      setIsResponding(false);
    }
  };

  const getTagLabel = (tag) => {
    // Use translation key if available, otherwise use English tag name directly
    const translationKey = `reviewTags.${tag}`;
    const translated = t(translationKey, { defaultValue: tag });
    // If translation returns the key (not found), use the tag as-is (English)
    return translated === translationKey ? tag : translated;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'hidden': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'อนุมัติแล้ว';
      case 'pending': return 'รออนุมัติ';
      case 'rejected': return 'ถูกปฏิเสธ';
      case 'hidden': return 'ซ่อน';
      default: return 'ไม่ทราบ';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="font-semibold text-lg text-gray-900">
              {review.title}
            </h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(review.status)}`}>
              {getStatusText(review.status)}
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <span className="text-yellow-500">{reviewUtils.formatRating(review.rating)}</span>
              <span>({reviewUtils.getRatingText(review.rating)})</span>
            </div>
            <span>โดย {review.reviewerName}</span>
            <span>{new Date(review.createdAt).toLocaleDateString('th-TH')}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        <p className="text-gray-700 leading-relaxed">
          {review.comment}
        </p>
      </div>

      {/* Tags */}
      {review.tags && review.tags.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {review.tags.map((tag) => {
              const translationKey = `reviewTags.${tag}`;
              const translated = t(translationKey, { defaultValue: tag });
              return (
                <span
                  key={tag}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                >
                  {translated}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Response Section */}
      {showResponse && (
        <div className="border-t pt-4">
          {review.response?.comment ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium text-gray-700">ตอบกลับ:</span>
                <span className="text-xs text-gray-500">
                  {new Date(review.response.respondedAt).toLocaleDateString('th-TH')}
                </span>
              </div>
              <p className="text-gray-700">{review.response.comment}</p>
            </div>
          ) : canRespond ? (
            <div>
              {!showResponseForm ? (
                <button
                  onClick={() => setShowResponseForm(true)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  ตอบกลับรีวิวนี้
                </button>
              ) : (
                <form onSubmit={handleRespond} className="space-y-3">
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="เขียนตอบกลับรีวิวนี้..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={500}
                  />
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      disabled={isResponding || !responseText.trim()}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                      {isResponding ? 'กำลังส่ง...' : 'ส่งตอบกลับ'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowResponseForm(false);
                        setResponseText('');
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ReviewCard;
